import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Grid,
  Divider,
} from "@mui/material";
import { TEMPLATE_OPTIONS } from "../types/invitationTypes";

const InvitationForm = ({ initialData, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    eventId: "",
    invitationDetails: {
      title: "",
      brideName: "",
      groomName: "",
      invitationMessage: "",
      eventDate: "",
      startTime: "",
      venueName: "",
      venueAddress: "",
      googleMapLink: "",
    },
    template: {
      templateId: "TMP001",
      templateName: "Royal Gold",
      primaryColor: "#D4AF37",
      secondaryColor: "#FFFFFF",
    },
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        eventId: initialData.eventId || "",
        invitationDetails: {
          title: initialData.invitationDetails?.title || "",
          brideName: initialData.invitationDetails?.brideName || "",
          groomName: initialData.invitationDetails?.groomName || "",
          invitationMessage:
            initialData.invitationDetails?.invitationMessage || "",
          eventDate: initialData.invitationDetails?.eventDate || "",
          startTime: initialData.invitationDetails?.startTime || "",
          venueName: initialData.invitationDetails?.venueName || "",
          venueAddress: initialData.invitationDetails?.venueAddress || "",
          googleMapLink: initialData.invitationDetails?.googleMapLink || "",
        },
        template: {
          templateId: initialData.template?.templateId || "TMP001",
          templateName: initialData.template?.templateName || "Royal Gold",
          primaryColor: initialData.template?.primaryColor || "#D4AF37",
          secondaryColor: initialData.template?.secondaryColor || "#FFFFFF",
        },
      });
    }
  }, [initialData]);

  const handleChange = (path, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      const keys = path.split(".");
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return updated;
    });
    if (errors[path]) setErrors((prev) => ({ ...prev, [path]: "" }));
  };

  const handleTemplateSelect = (templateId) => {
    const tpl = TEMPLATE_OPTIONS.find((t) => t.id === templateId);
    if (tpl) {
      setFormData((prev) => ({
        ...prev,
        template: {
          templateId: tpl.id,
          templateName: tpl.name,
          primaryColor: tpl.primaryColor,
          secondaryColor: tpl.secondaryColor,
        },
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.invitationDetails.title.trim())
      newErrors["invitationDetails.title"] = "Title is required";
    if (!formData.invitationDetails.brideName.trim())
      newErrors["invitationDetails.brideName"] = "Bride name is required";
    if (!formData.invitationDetails.groomName.trim())
      newErrors["invitationDetails.groomName"] = "Groom name is required";
    if (!formData.invitationDetails.eventDate)
      newErrors["invitationDetails.eventDate"] = "Event date is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Invitation Title *"
          value={formData.invitationDetails.title}
          onChange={(e) =>
            handleChange("invitationDetails.title", e.target.value)
          }
          error={!!errors["invitationDetails.title"]}
          helperText={errors["invitationDetails.title"]}
          fullWidth
          disabled={loading}
          placeholder="e.g., Akash ❤️ Priya Wedding"
        />

        <TextField
          label="Linked Event ID"
          value={formData.eventId}
          onChange={(e) => handleChange("eventId", e.target.value)}
          fullWidth
          disabled={loading}
          placeholder="EVT001"
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Groom Name *"
              fullWidth
              disabled={loading}
              value={formData.invitationDetails.groomName}
              onChange={(e) =>
                handleChange("invitationDetails.groomName", e.target.value)
              }
              error={!!errors["invitationDetails.groomName"]}
              helperText={errors["invitationDetails.groomName"]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Bride Name *"
              fullWidth
              disabled={loading}
              value={formData.invitationDetails.brideName}
              onChange={(e) =>
                handleChange("invitationDetails.brideName", e.target.value)
              }
              error={!!errors["invitationDetails.brideName"]}
              helperText={errors["invitationDetails.brideName"]}
            />
          </Grid>
        </Grid>

        <TextField
          label="Invitation Message"
          multiline
          rows={3}
          fullWidth
          disabled={loading}
          value={formData.invitationDetails.invitationMessage}
          onChange={(e) =>
            handleChange("invitationDetails.invitationMessage", e.target.value)
          }
          placeholder="With great joy, we invite you to celebrate our wedding..."
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Event Date *"
              type="date"
              fullWidth
              disabled={loading}
              InputLabelProps={{ shrink: true }}
              value={formData.invitationDetails.eventDate}
              onChange={(e) =>
                handleChange("invitationDetails.eventDate", e.target.value)
              }
              error={!!errors["invitationDetails.eventDate"]}
              helperText={errors["invitationDetails.eventDate"]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Start Time"
              fullWidth
              disabled={loading}
              placeholder="06:00 AM"
              value={formData.invitationDetails.startTime}
              onChange={(e) =>
                handleChange("invitationDetails.startTime", e.target.value)
              }
            />
          </Grid>
        </Grid>

        <Divider>
          <Typography variant="caption">Venue Details</Typography>
        </Divider>

        <TextField
          label="Venue Name"
          fullWidth
          disabled={loading}
          value={formData.invitationDetails.venueName}
          onChange={(e) =>
            handleChange("invitationDetails.venueName", e.target.value)
          }
          placeholder="ABC Mahal"
        />
        <TextField
          label="Venue Address"
          fullWidth
          disabled={loading}
          value={formData.invitationDetails.venueAddress}
          onChange={(e) =>
            handleChange("invitationDetails.venueAddress", e.target.value)
          }
          placeholder="Orathanadu, Thanjavur"
        />
        <TextField
          label="Google Maps Link"
          fullWidth
          disabled={loading}
          value={formData.invitationDetails.googleMapLink}
          onChange={(e) =>
            handleChange("invitationDetails.googleMapLink", e.target.value)
          }
          placeholder="https://maps.google.com/..."
        />

        <Divider>
          <Typography variant="caption">Template</Typography>
        </Divider>

        <FormControl fullWidth>
          <InputLabel>Select Template</InputLabel>
          <Select
            value={formData.template.templateId}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            label="Select Template"
            disabled={loading}
          >
            {TEMPLATE_OPTIONS.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: formData.template.primaryColor,
              border: "1px solid #ccc",
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Primary: {formData.template.primaryColor}
          </Typography>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: formData.template.secondaryColor,
              border: "1px solid #ccc",
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Secondary: {formData.template.secondaryColor}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : initialData
                ? "Update Invitation"
                : "Create Invitation"}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default InvitationForm;
